/**
 * User Bootstrap Data Service
 *
 * Application service responsible for hydrating all core user-facing datasets
 * needed by the global store during app initialization.
 *
 * Why this service exists
 * - Keeps `store-provider` focused on lifecycle and state commits.
 * - Centralizes cross-resource user hydration in one predictable contract.
 * - Provides a testable boundary around API orchestration + mapping.
 *
 * Scope
 * - Fetches scorecards, availables, and badges.
 * - Maps API payloads into frontend domain shapes used by store slices.
 * - Derives personas from the availables payload.
 */
import { FEATURE_FLAGS } from '@/constants';
import { IPersona } from '@/types';
import {
  getScoreCardsByUser,
  getScoreCardsByUserV2,
  getUserAvailables,
  getUserCertificationBadges,
} from '@/lib';
import {
  mapApiAvailablesToFrontend,
  mapApiScorecardsToFrontend,
  mapApiSimulationsToFrontend,
  mapApiUserBadgesToFrontend,
} from '@/utils';

/**
 * Stable return contract for user bootstrap hydration.
 *
 * Notes
 * - Types are inferred from existing mapper return signatures via `ReturnType`
 *   so this contract evolves safely with mapper output types.
 * - `personas` are derived directly from availables and intentionally returned
 *   as a dedicated slice for store initialization convenience.
 */
export interface UserBootstrapData {
  /** Mapped user badge models for profile and achievement surfaces. */
  badges: ReturnType<typeof mapApiUserBadgesToFrontend>;
  /** Mapped certification models enriched from availables + scorecards. */
  certifications: ReturnType<typeof mapApiAvailablesToFrontend>;
  /** Flattened persona list collected from all available certification bundles. */
  personas: IPersona[];
  /** Mapped score entities used for history, pass/fail, and detail views. */
  scores: ReturnType<typeof mapApiScorecardsToFrontend>;
  /** Mapped simulation models with score-linked history metadata. */
  simulations: ReturnType<typeof mapApiSimulationsToFrontend>;
}

/**
 * Bootstrap all core user datasets in one operation.
 *
 * Execution strategy
 * 1. Select scorecard endpoint (v1/v2) via feature flag.
 * 2. Fetch independent resources in parallel with `Promise.all`.
 * 3. Transform raw API payloads into frontend-safe models.
 * 4. Return a single object that callers can commit into store slices.
 *
 * @param userId Authenticated user UUID.
 * @returns Hydrated and mapped user bootstrap data.
 */
export const bootstrapUserData = async (userId: string): Promise<UserBootstrapData> => {
  // ================================
  // Step 1: Fetch Raw API Data (parallel)
  // ================================
  // These calls are independent, so running in parallel minimizes bootstrap time.
  // Scorecard endpoint selection is feature-flagged to support v1/v2 migration.
  const [apiScorecards, apiAvailables, apiUserBadges] = await Promise.all([
    FEATURE_FLAGS.ENABLE_SCORECARD_V2
      ? getScoreCardsByUserV2(userId, false)
      : getScoreCardsByUser(userId),
    getUserAvailables(userId),
    getUserCertificationBadges(userId),
  ]);

  // ================================
  // Step 2: Map + Derive Frontend Models
  // ================================
  // Mapping functions encapsulate API-to-frontend translation logic.
  // Returning mapped models from this service keeps call sites orchestration-only.
  return {
    badges: mapApiUserBadgesToFrontend({ apiUserBadges }),
    certifications: mapApiAvailablesToFrontend({ apiAvailables, apiScorecards }),
    // Personas are nested under availables; flatten for direct store consumption.
    personas: apiAvailables.flatMap((available) => available.personas || []),
    scores: mapApiScorecardsToFrontend({ apiAvailables, apiScorecards }),
    simulations: mapApiSimulationsToFrontend({ apiAvailables, apiScorecards }),
  };
};
